/**
 * Invite codes (R12): 8 chars of Crockford base32, shown as XXXX-XXXX, case-insensitive,
 * hyphen optional. Relogin codes are 6 chars. The code carried through install is a bonus:
 * the gate shows it in big type, Welcome offers Paste and typed entry.
 */

const CROCKFORD = /^[0-9A-HJKMNP-TV-Z]+$/;
const PENDING_KEY = 'tts.pendingInvite';

/** Uppercase, drop spaces and hyphens, map the look-alikes Crockford allows (O to 0, I and L to 1). */
export function normalizeCode(raw: string): string {
  return raw
    .toUpperCase()
    .replace(/[\s-]+/g, '')
    .replace(/O/g, '0')
    .replace(/[IL]/g, '1');
}

/** Pulls a code out of pasted text: a raw code, "XXXX-XXXX", or a full /j/<code> link. */
export function extractCode(text: string): string | null {
  const trimmed = text.trim();
  const link = /\/j\/([0-9A-Za-z-]{6,12})/.exec(trimmed);
  const candidate = normalizeCode(link?.[1] ?? trimmed);
  if (candidate.length !== 6 && candidate.length !== 8) return null;
  return CROCKFORD.test(candidate) ? candidate : null;
}

export function isInviteCode(code: string): boolean {
  return code.length === 8 && CROCKFORD.test(code);
}

export function isReloginCode(code: string): boolean {
  return code.length === 6 && CROCKFORD.test(code);
}

/** "K7QM4PZD" → "K7QM-4PZD". Relogin codes stay as they are. */
export function formatCode(code: string): string {
  const c = normalizeCode(code);
  return c.length === 8 ? `${c.slice(0, 4)}-${c.slice(4)}` : c;
}

/** The code in a /j/:code path, or null. */
export function codeFromPath(pathname: string): string | null {
  const m = /^\/j\/([^/?#]+)/.exec(pathname);
  if (!m?.[1]) return null;
  return extractCode(decodeURIComponent(m[1]));
}

export function rememberPendingCode(code: string): void {
  try {
    localStorage.setItem(PENDING_KEY, code);
  } catch {
    // Private mode or blocked storage: the big code on screen is the real path anyway.
  }
}

export function readPendingCode(): string | null {
  try {
    return localStorage.getItem(PENDING_KEY);
  } catch {
    return null;
  }
}

export function clearPendingCode(): void {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    // ignore
  }
}
